import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { Controller } from 'react-hook-form';

import { useOrganizationsPaginated } from '@/pages/members/hooks/useOrganizations';
import { ReferralOrganizationDropdownProps } from '@/pages/members/pages/Onboarding/steps/HowDidYouHearReferralStep/types';
import Input from '@/ui/members/components/form/Input';
import SelectDialog from '@/ui/members/components/form/SelectDialog';
import Tooltip from '@/ui/members/components/Tooltip';

const ReferralOrganizationDropdown = ({
  t,
  control,
  isReferralSourceLead,
  values
}: ReferralOrganizationDropdownProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const {
    data: paginatedOrganizations,
    fetchNextPage,
    hasNextPage: hasNextPageReferrerOrganizations,
    isFetchingNextPage: isFetchingReferrerOrganizations,
    isLoading: isLoadingReferrerOrganizations
  } = useOrganizationsPaginated({
    params: { search: debouncedSearchQuery }
  });

  const otherOption = useMemo(() => ({ label: t('other'), value: 'other' }), [t]);

  const organizationsWithOtherOption = useMemo(() => {
    if (!paginatedOrganizations?.organizations) return [otherOption];
    return [...paginatedOrganizations.organizations, otherOption];
  }, [paginatedOrganizations?.organizations, otherOption]);

  const handleSearch = useCallback((query: string) => setSearchQuery(query), []);

  if (!isReferralSourceLead) {
    return null;
  }

  return (
    <div className="grid gap-2 my-4">
      <Controller
        name="referrerId"
        control={control}
        render={({ field }) => (
          <SelectDialog
            id="referrer-id"
            isLoading={isFetchingReferrerOrganizations || isLoadingReferrerOrganizations}
            label={t('chooseOrganization')}
            onChange={field.onChange}
            onSearch={handleSearch}
            options={organizationsWithOtherOption}
            placeholder={t('referrerNamePlaceholder')}
            selected={field.value}
            scrollPagination={{
              hasNextPage: hasNextPageReferrerOrganizations,
              isFetchingNextPage: isFetchingReferrerOrganizations,
              fetchNextPage
            }}
          />
        )}
      />
      {values.referrerId === 'other' && (
        <Controller
          name="referrerName"
          control={control}
          render={({ field: referrerField }) => (
            <Input
              id="referrer-name"
              onChange={referrerField.onChange}
              placeholder={t('enterOrgName')}
              value={referrerField.value}
              type="text"
            />
          )}
        />
      )}
      {values.referrerId !== 'other' && (
        <Tooltip tooltipContent={t('tooltipText')}>
          <p className="flex items-center justify-center text-sm text-uniform-gray-300">
            {t('cantFindOrg')} <FontAwesomeIcon className="ml-2" icon={faInfoCircle} />
          </p>
        </Tooltip>
      )}
    </div>
  );
};

export default ReferralOrganizationDropdown;
